import { useEffect, useState } from "react";
import api from "@/utils/api";
import { Calendar, CheckCircle, Clock, MapPin } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { getToken, getUser } from "@/utils/auth";
import { useSettings } from "@/context/SettingsContext";

const MemberAttendance = () => {
  const { formatDate } = useSettings();
  const user = getUser();
  const [loading, setLoading] = useState(true);
  const [attendance, setAttendance] = useState([]);

  useEffect(() => {
    fetchMemberData();
  }, []);

  const fetchMemberData = async () => {
    try {
      const response = await api.get('/members/me');
      
      const member = response.data.member || (response.data.id ? response.data : null);
      if (member) {
        // Sort attendance by date descending
        const sortedAttendance = (member.attendance || []).sort((a, b) => 
          new Date(b.date) - new Date(a.date)
        );
        setAttendance(sortedAttendance);
      } else {
        // Fallback logic
        const membersResp = await api.get('/members');
        const candidate = membersResp.data.find((m) => 
          (user?.email && (m.email || '').toLowerCase() === (user.email || '').toLowerCase())
        );
        
        if (candidate) {
          const detailResp = await api.get(`/members/${candidate.id}`);
          const sortedAttendance = (detailResp.data.attendance || []).sort((a, b) => 
            new Date(b.date) - new Date(a.date)
          );
          setAttendance(sortedAttendance);
        }
      }
      setLoading(false);
    } catch (error) {
      console.error("Failed to load attendance:", error);
      const detail = error.response?.data?.detail || "";
      if (error.response?.status === 404 && detail.toLowerCase().includes("member not found")) {
        try {
          const membersResp = await api.get('/members');
          const candidate = membersResp.data.find((m) => 
            (user?.email && (m.email || '').toLowerCase() === (user.email || '').toLowerCase())
          );
          
          if (candidate) {
            const detailResp = await api.get(`/members/${candidate.id}`);
            const sortedAttendance = (detailResp.data.attendance || []).sort((a, b) => 
              new Date(b.date) - new Date(a.date)
            );
            setAttendance(sortedAttendance);
          } else {
            setAttendance([]);
          }
        } catch (e) {
          setAttendance([]);
        }
        setLoading(false);
      } else {
        toast.error("Failed to load attendance history");
        setLoading(false);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lime-500"></div>
      </div>
    );
  }

  // Calculate stats
  const totalVisits = attendance.length;
  const thisMonthVisits = attendance.filter(a => {
    const date = new Date(a.date);
    const now = new Date();
    return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
  }).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
            My Attendance
          </h1>
          <p className="text-zinc-500 mt-1">Track your gym visits and activity</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border border-zinc-200 rounded-xl p-6 shadow-sm flex items-center gap-4">
          <div className="p-3 bg-blue-100 text-blue-600 rounded-full">
            <CheckCircle size={24} />
          </div>
          <div>
            <p className="text-sm text-zinc-500 font-medium uppercase">Total Visits</p>
            <p className="text-2xl font-bold text-zinc-900">{totalVisits}</p>
          </div>
        </div>
        <div className="bg-white border border-zinc-200 rounded-xl p-6 shadow-sm flex items-center gap-4">
          <div className="p-3 bg-lime-100 text-lime-600 rounded-full">
            <Calendar size={24} />
          </div>
          <div>
            <p className="text-sm text-zinc-500 font-medium uppercase">This Month</p>
            <p className="text-2xl font-bold text-zinc-900">{thisMonthVisits}</p>
          </div>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden shadow-sm">
        <div className="px-6 py-4 border-b border-zinc-200 bg-zinc-50/50">
          <h2 className="font-semibold text-zinc-900">Visit History</h2>
        </div>
        <div className="divide-y divide-zinc-200">
          {attendance.length === 0 ? (
            <div className="px-6 py-12 text-center text-zinc-500">
              No attendance records found
            </div>
          ) : (
            attendance.map((record, index) => (
              <div key={index} className="px-6 py-4 hover:bg-zinc-50 transition-colors flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-zinc-100 rounded-lg text-zinc-500">
                    <Calendar size={20} />
                  </div>
                  <div>
                    <div className="font-medium text-zinc-900">{formatDate(record.date)}</div>
                    <div className="text-sm text-zinc-500 flex items-center gap-2 mt-1">
                      <Clock size={14} />
                      {new Date(record.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 pl-14 md:pl-0">
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 flex items-center gap-1">
                    <CheckCircle size={12} />
                    Present
                  </Badge>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default MemberAttendance;
